import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, 
  ArrowRight, 
  Star, 
  Calendar, 
  Eye, 
  Building2, 
  Search,
  Loader2,
  BookOpen,
  FileText,
  ChevronRight,
  Filter
} from 'lucide-react';
import api from '@/services/api';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CaseStudiesPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Seed the type filter from the URL (?type=case_study | blog) so
  // homepage CTAs like "Read Case Studies" / "Visit Our Blog" land on the
  // right filter. Also handle the /blog route as an implicit type=blog
  // default so the URL alone communicates intent.
  const _typeFromUrl = searchParams.get('type') || (location.pathname === '/blog' ? 'blog' : '');
  const [selectedType, setSelectedType] = useState(_typeFromUrl);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [singleItem, setSingleItem] = useState(null);

  // Always land at the top of the page — previously the browser restored
  // the scroll position from wherever the user was on the homepage,
  // which felt like the blog page "auto-scrolled to the footer".
  useEffect(() => {
    if (!slug) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  // Keep selectedType in sync when the user navigates between /blog and
  // /case-studies (or changes ?type= via a shared link) without leaving
  // the SPA. Fixes the review nit where /blog → /case-studies without a
  // query left the state stuck on 'blog'.
  useEffect(() => {
    if (slug) return;
    const t = searchParams.get('type') || (location.pathname === '/blog' ? 'blog' : '');
    setSelectedType(t);
  }, [location.pathname, searchParams, slug]);
  
  useEffect(() => {
    if (slug) {
      loadSingleItem(slug);
    } else {
      loadItems();
    }
  }, [slug, selectedType, selectedIndustry]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('post_type', selectedType);
      if (selectedIndustry) params.append('industry', selectedIndustry);
      // Bumped from 20 → 100 so all blogs / case studies fit on one page
      // for now. If the library grows past ~100 we'll add pagination.
      params.append('limit', '100');

      const response = await fetch(`${API_URL}/api/case-studies/public?${params.toString()}`);
      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error('Failed to load items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Keep the URL in sync with the filter selection so the current view
  // is shareable and browser back/forward works naturally.
  const changeType = (nextType) => {
    setSelectedType(nextType);
    const nextParams = new URLSearchParams(searchParams);
    if (nextType) nextParams.set('type', nextType);
    else nextParams.delete('type');
    setSearchParams(nextParams, { replace: true });
  };
  
  const loadSingleItem = async (itemSlug) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/case-studies/public/${itemSlug}`);
      if (response.ok) {
        const data = await response.json();
        setSingleItem(data);
      } else {
        navigate('/case-studies');
      }
    } catch (error) {
      navigate('/case-studies');
    } finally {
      setLoading(false);
    }
  };
  
  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Single item view
  if (slug && singleItem) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <PublicHeader />
        {/* Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/case-studies')}
              className="text-slate-600 hover:text-slate-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to all content
            </Button>
            
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline" className={singleItem.post_type === 'case_study' ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-blue-300 text-slate-900 bg-slate-50'}>
                {singleItem.post_type === 'case_study' ? 'Case Study' : 'Blog Post'}
              </Badge>
              {singleItem.is_featured && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                  <Star className="w-3 h-3 mr-1 fill-amber-500" />
                  Featured
                </Badge>
              )}
              {singleItem.industry && (
                <Badge variant="outline" className="border-slate-300 text-slate-600">{singleItem.industry}</Badge>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{singleItem.title}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              {singleItem.company_name && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {singleItem.company_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(singleItem.published_at || singleItem.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {singleItem.view_count || 0} views
              </span>
            </div>
          </div>
        </div>
        
        {/* Featured Image */}
        {singleItem.featured_image && (
          <div className="max-w-4xl mx-auto px-4 py-8">
            <img 
              src={singleItem.featured_image} 
              alt={singleItem.title}
              className="w-full h-64 md:h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        )}
        
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-8">
            <p className="text-lg text-slate-700 leading-relaxed">{singleItem.summary}</p>
          </div>
          
          {/* Results (for case studies) */}
          {singleItem.results && singleItem.results.length > 0 && (
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {singleItem.results.map((result, i) => (
                <div key={`res-${i}-${result.metric}`} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-slate-900 mb-2">{result.metric}</p>
                  <p className="text-sm text-slate-600">{result.description}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Main Content */}
          <article className="prose prose-slate max-w-none">
            {singleItem.content.split('\n').map((paragraph, i) => {
              const key = `p-${i}-${paragraph.slice(0, 12)}`;
              if (paragraph.startsWith('# ')) {
                return <h1 key={key} className="text-2xl font-bold text-slate-900 mt-8 mb-4">{paragraph.slice(2)}</h1>;
              }
              if (paragraph.startsWith('## ')) {
                return <h2 key={key} className="text-xl font-bold text-slate-900 mt-6 mb-3">{paragraph.slice(3)}</h2>;
              }
              if (paragraph.startsWith('### ')) {
                return <h3 key={key} className="text-lg font-semibold text-slate-900 mt-4 mb-2">{paragraph.slice(4)}</h3>;
              }
              if (paragraph.startsWith('- ')) {
                return <li key={key} className="text-slate-700 ml-4">{paragraph.slice(2)}</li>;
              }
              if (paragraph.trim()) {
                return <p key={key} className="text-slate-700 leading-relaxed mb-4">{paragraph}</p>;
              }
              return null;
            })}
          </article>
          
          {/* Tags */}
          {singleItem.tags && singleItem.tags.length > 0 && (
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-500">Tags:</span>
                {singleItem.tags.map((tag, i) => (
                  <Badge key={`tag-${i}-${tag}`} variant="outline" className="border-slate-300 text-slate-600">{tag}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-3">Ready to transform your business?</h3>
            <p className="text-blue-100 mb-6">See how Harmonix can help you achieve similar results</p>
            <Button onClick={() => navigate('/')} className="bg-white text-slate-900 hover:bg-slate-50">
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }
  
  // List view
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />
      {/* Hero */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Case Studies & Insights</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Discover how businesses are transforming customer experience with AI-powered agents
          </p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 -mt-8">
        <Card className="bg-white border-slate-200 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search case studies and blog posts..."
                  className="pl-10 bg-white border-slate-300 text-slate-900"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={selectedType === '' ? 'default' : 'outline'}
                  onClick={() => changeType('')}
                  className={selectedType === '' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-300 text-slate-700'}
                >
                  All
                </Button>
                <Button
                  variant={selectedType === 'case_study' ? 'default' : 'outline'}
                  onClick={() => changeType('case_study')}
                  className={selectedType === 'case_study' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-300 text-slate-700'}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Case Studies
                </Button>
                <Button
                  variant={selectedType === 'blog' ? 'default' : 'outline'}
                  onClick={() => changeType('blog')}
                  className={selectedType === 'blog' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-300 text-slate-700'}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Blog
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Content Grid */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-slate-900 animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">No content found</p>
            <p className="text-slate-500 mt-2">Check back soon for new case studies and blog posts</p>
          </div>
        ) : (
          <>
            {/* Featured Items — show every item flagged is_featured (no
                arbitrary cap). Editor controls which items go here. */}
            {filteredItems.filter(i => i.is_featured).length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                  Featured
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {filteredItems.filter(i => i.is_featured).map((item) => (
                    <Card 
                      key={item.case_study_id} 
                      className="bg-white border-slate-200 hover:shadow-lg transition-shadow cursor-pointer overflow-hidden group"
                      onClick={() => navigate(`/case-studies/${item.slug}`)}
                    >
                      {item.featured_image ? (
                        <div className="aspect-[16/9] overflow-hidden bg-slate-50">
                          <img 
                            src={item.featured_image} 
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="aspect-[16/9] bg-gradient-to-br from-slate-50 to-slate-100" />
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className={item.post_type === 'case_study' ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-blue-300 text-slate-900 bg-slate-50'}>
                            {item.post_type === 'case_study' ? 'Case Study' : 'Blog'}
                          </Badge>
                          {item.industry && (
                            <Badge variant="outline" className="border-slate-300 text-slate-600">{item.industry}</Badge>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-slate-900 transition-colors">{item.title}</h3>
                        <p className="text-slate-600 line-clamp-2 mb-4">{item.summary}</p>
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <span>{new Date(item.created_at).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1 text-slate-900 group-hover:gap-2 transition-all">
                            Read more <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {/* All Items */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {selectedType === 'case_study' ? 'All Case Studies' : selectedType === 'blog' ? 'All Blog Posts' : 'All Content'}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Show every non-featured item. Previously this used a
                    confusing conditional that hid non-featured items when
                    3+ featured existed. Now the "All" grid is exactly
                    what its label says. */}
                {filteredItems.filter(i => !i.is_featured).map((item) => (
                  <Card 
                    key={item.case_study_id} 
                    className="bg-white border-slate-200 hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/case-studies/${item.slug}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className={item.post_type === 'case_study' ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-blue-300 text-slate-900 bg-slate-50'}>
                          {item.post_type === 'case_study' ? 'Case Study' : 'Blog'}
                        </Badge>
                        {item.is_featured && (
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-slate-900 transition-colors line-clamp-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm line-clamp-3 mb-4">{item.summary}</p>
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>{item.view_count || 0} views</span>
                        <span className="flex items-center gap-1 text-slate-900">
                          Read <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* CTA Section */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to write your success story?</h2>
          <p className="text-slate-400 mb-8">Join hundreds of businesses using Harmonix to transform customer engagement</p>
          <Button onClick={() => navigate('/')} size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
            Start Your Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default CaseStudiesPage;
